import { FC, useMemo } from "react";
import { DialogContent, Typography } from "@mui/material";
import { useFormik } from "formik";
import { v4 as uuid } from "uuid";
import { useWillUnmount } from "rooks";
import { FiltersDialogActions } from "./FiltersDialogActions";
import { FilterDialogList } from "./FilterDialogList";
import { FilterDialogListActionButtons } from "./FilterDialogListActionButtons";
import {
  FilterKeyType,
  ResourceFilters,
  ResourceType,
} from "../../../../../../services/cloudchipr.api";
import { filtersValidationSchema } from "../utils/constants/validation";
import { FiltersInitialValues } from "../utils/types/types";
import { commonExcludedFilters } from "../../../../account/components/resource-card/resource-card-content/resource-card-data/utils/constants";

interface FiltersDialogContentProps {
  onClose(): void;
  accountIds?: string[];
  hideRevert?: boolean;
  resourceType: ResourceType;
  excludedFilters?: FilterKeyType[];
  onSubmit(values: ResourceFilters): void;
  filters: ResourceFilters;
  defaultFilters: ResourceFilters;
}

export const FiltersDialogContent: FC<FiltersDialogContentProps> = ({
  resourceType,
  onClose,
  onSubmit,
  accountIds,
  excludedFilters,
  filters,
  defaultFilters,
  hideRevert,
}) => {
  const initialValues: FiltersInitialValues = useMemo(() => {
    return {
      ...filters,
      filter_items: filters.filter_items.map((filterItem) => ({
        ...filterItem,
        id: uuid(),
      })),
    };
  }, [filters]);

  const formik = useFormik({
    initialValues,
    validationSchema: filtersValidationSchema,
    onSubmit: (values) => {
      onSubmit({
        ...values,
        filter_items: values.filter_items.map(({ id, ...filter }) => ({
          ...filter,
        })),
      });
      onClose();
    },
  });

  const {
    dirty,
    values,
    errors,
    isValid,
    setValues,
    resetForm,
    handleSubmit,
    setFieldValue,
  } = formik;

  useWillUnmount(resetForm);

  const combinedExcludedFilters = useMemo(() => {
    return (excludedFilters ?? [])?.concat(commonExcludedFilters);
  }, [excludedFilters]);

  return (
    <form onSubmit={handleSubmit}>
      <DialogContent dividers>
        {values.filter_items.length ? (
          <FilterDialogList
            errors={errors}
            setValues={setValues}
            accountIds={accountIds}
            defaultFilters={defaultFilters}
            resourceType={resourceType}
            operators={values.operators}
            setFieldValue={setFieldValue}
            filterItems={values.filter_items}
            filterGroups={values.filter_groups}
            excludedFilters={combinedExcludedFilters}
          />
        ) : (
          <Typography>No filters applied</Typography>
        )}

        <FilterDialogListActionButtons
          filters={values}
          hideRevert={hideRevert}
          setValues={setValues}
          defaultFilters={defaultFilters}
          resourceType={resourceType}
        />
      </DialogContent>

      <FiltersDialogActions onClose={onClose} disabled={!dirty || !isValid} />
    </form>
  );
};
